import { AuthenticationResult } from './lib/types'

export const parseAuthenticationResult = (
  queryString: string
): AuthenticationResult => {
  if (queryString.indexOf('#') > -1) {
    queryString = queryString.substring(0, queryString.indexOf('#'))
  }

  const searchParams = new URLSearchParams(queryString)

  return {
    state: searchParams.get('state')!,
    code: searchParams.get('code') || undefined,
    error: searchParams.get('error') || undefined,
    error_description: searchParams.get('error_description') || undefined
  }
}

const stripUndefined = (params: any) => {
  return Object.keys(params)
    .filter(k => typeof params[k] !== 'undefined')
    .reduce((acc, key) => ({ ...acc, [key]: params[key] }), {})
}

export const createQueryParams = ({ clientId: client_id, ...params }: any) => {
  return new URLSearchParams(
    stripUndefined({ client_id, ...params })
  ).toString()
}

/**
 * @ignore
 */
export const getTokenIssuer = (
  issuer: string | undefined,
  domainUrl: string
) => {
  if (issuer) {
    return issuer.startsWith('https://') ? issuer : `https://${issuer}`
  }

  return `${domainUrl}`
}

// Un tenant en loopback es el único que se sirve sin TLS: un auth de
// desarrollo en `localhost:8080`. Todo lo demás es un host remoto.
const isLoopbackHost = (raw: string) => {
  const authority = raw.split('/')[0]
  // IPv6 va entre corchetes (`[::1]:8080`); el resto parte por el puerto.
  const host = authority.startsWith('[')
    ? authority.slice(0, authority.indexOf(']') + 1).toLowerCase()
    : authority.split(':')[0].toLowerCase()

  return (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host === '[::1]' ||
    /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)
  )
}

// Normaliza el `domain` para que dé igual cómo lo pase el usuario: con o sin
// protocolo, con trailing slash, o incluso con protocolo duplicado (el valor
// que el dashboard copia ya trae `https://`). Siempre devuelve
// `<protocol>://<host>` sin barra final.
export const getDomain = (domainUrl: string) => {
  const raw = (domainUrl ?? '').trim().replace(/\/+$/, '')
  // Usuario pasó protocolo (uno o varios, p.ej. "https://https://x"):
  // preservar http/https intencionado y descartar duplicados.
  const match = raw.match(/^(https?):\/\/(.*)$/i)
  if (match) {
    const host = match[2].replace(/^(https?:\/\/)+/i, '')
    return `${match[1].toLowerCase()}://${host}`
  }
  // Sin protocolo: `https`, salvo un tenant en loopback.
  //
  // Aquí NO se hereda `location.protocol`: una app servida por http en
  // localhost apuntaría al :80 del tenant, que no atiende y devuelve un 404
  // pelado sin cabeceras CORS — el navegador lo reporta como error de CORS y
  // el fallo no se reproduce en producción, donde la app ya va por https.
  return `${isLoopbackHost(raw) ? 'http' : 'https'}://${raw}`
}

export const encode = (value: string) => btoa(value)
export const decode = (value: string) => atob(value)

// https://stackoverflow.com/questions/30106476/
const decodeB64 = (input: string) =>
  decodeURIComponent(
    atob(input)
      .split('')
      .map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

const urlEncodeB64 = (input: string) => {
  const b64Chars: { [index: string]: string } = { '+': '-', '/': '_', '=': '' }
  return input.replace(/[+/=]/g, (m: string) => b64Chars[m])
}

export const urlDecodeB64 = (input: string) =>
  decodeB64(input.replace(/_/g, '/').replace(/-/g, '+'))

export const bufferToBase64UrlEncoded = (input: number[] | Uint8Array) => {
  const ie11SafeInput = new Uint8Array(input)
  return urlEncodeB64(
    window.btoa(String.fromCharCode(...Array.from(ie11SafeInput)))
  )
}

export const parseNumber = (value: any): number | undefined => {
  if (typeof value !== 'string') {
    return value
  }
  return parseInt(value, 10) || undefined
}

/**
 * @ignore
 */
export const buildIsAuthenticatedCookieName = (clientId: string) =>
  `auth0.${clientId}.is.authenticated`
